import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const requestTypeQuestions: Record<string, { label: string; name: string; type?: string; required?: boolean; options?: string[]; showIf?: { field: string; value: string | string[] } }[]> = {
  'graphic-design': [
    { label: 'What type of graphic design do you need?', name: 'type', type: 'select', required: true, options: ['Logo', 'Social Post', 'Flyer', 'Poster', 'Infographic'] },
    { label: 'Describe your project', name: 'description', required: true },
    { label: 'Do you have brand assets?', name: 'brand_assets', type: 'file' },
    // Conditional questions for Logo
    { label: 'Do you want a text-based logo, icon-based, or a combination?', name: 'logo_style', type: 'select', required: false, options: ['Text-based', 'Icon-based', 'Combination'], showIf: { field: 'type', value: 'Logo' } },
    { label: 'Should the logo be modern, vintage, minimal, abstract, etc.?', name: 'logo_aesthetic', type: 'text', required: false, showIf: { field: 'type', value: 'Logo' } },
    { label: 'Where will you use the logo? (Print, web, app, merch)', name: 'logo_usage', type: 'textarea', required: false, showIf: { field: 'type', value: 'Logo' } },
    // Conditional questions for Social Post
    { label: 'What platforms? (Instagram, LinkedIn, Facebook, TikTok, etc.)', name: 'social_platforms', type: 'textarea', required: false, showIf: { field: 'type', value: 'Social Post' } },
    { label: 'What size(s) or formats do you need?', name: 'social_sizes', type: 'text', required: false, showIf: { field: 'type', value: 'Social Post' } },
    { label: 'What kind of content: quote templates, product promos, event banners?', name: 'social_content_type', type: 'text', required: false, showIf: { field: 'type', value: 'Social Post' } },
    // Conditional questions for Flyer/Poster
    { label: 'What is the main message?', name: 'flyer_message', type: 'textarea', required: false, showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    { label: 'Is this for print or digital distribution?', name: 'flyer_distribution', type: 'select', required: false, options: ['Print', 'Digital', 'Both'], showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    { label: 'What size/dimensions?', name: 'flyer_dimensions', type: 'text', required: false, showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    { label: 'Do you need QR codes, contact info, or social handles included?', name: 'flyer_elements', type: 'textarea', required: false, showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    { label: 'Any required logos, text, or imagery?', name: 'flyer_requirements', type: 'textarea', required: false, showIf: { field: 'type', value: ['Flyer', 'Poster'] } },
    // Common questions for all types
    { label: 'What file formats do you need? (PDF, PNG, PSD, SVG, etc.)', name: 'file_formats', type: 'textarea', required: true },
    { label: 'Do you have a website or able to upload your inspiration?', name: 'inspiration_source', type: 'text', required: false },
    { label: 'Do you have any competitors?', name: 'competitors', type: 'textarea', required: false },
  ],
  'web-design': [
    { label: 'What type of web design do you need?', name: 'type', type: 'select', required: true, options: ['Landing Page', 'Microsite', 'Website Mockup'] },
    { label: 'What is the purpose of the website or landing page? (e.g., lead generation, product launch, portfolio, sales)', name: 'website_purpose', type: 'select', required: true, options: ['Lead Generation', 'Product Launch', 'Portfolio', 'Sales', 'Information', 'Other'] },
    { label: 'Who is your target audience?', name: 'target_audience', type: 'textarea', required: true },
    { label: 'What action should users take? (Call, sign up, purchase, download?)', name: 'user_action', type: 'text', required: true },
    { label: 'Do you have a sitemap or list of sections you want? (e.g., Hero, Features, Testimonials, Pricing, Contact)', name: 'sitemap_sections', type: 'textarea', required: true },
    { label: 'Do you have all the copy (text), or will you need help writing it?', name: 'copy_status', type: 'select', required: true, options: ['All copy ready', 'Need help writing', 'Mix of both'] },
    { label: 'Do you have photos, illustrations, or branding assets ready?', name: 'assets_status', type: 'select', required: true, options: ['All assets ready', 'Need some assets', 'Need all assets'] },
    { label: 'Do you have brand guidelines (colors, fonts, logos)?', name: 'brand_guidelines', type: 'select', required: true, options: ['Yes, complete guidelines', 'Partial guidelines', 'No guidelines'] },
    { label: 'Are there websites or landing pages you love or hate? Why?', name: 'reference_websites', type: 'textarea', required: false },
  ],
  'ui-ux-design': [
    { label: 'What type of UI/UX design do you need?', name: 'type', type: 'select', required: true, options: ['App Flow', 'Wireframe', 'User Journey'] },
    { label: 'What platform is this for?', name: 'platform', required: true },
    { label: 'Describe the user journey', name: 'user_journey' },
  ],
  'branding': [
    { label: 'What type of branding do you need?', name: 'type', type: 'select', required: true, options: ['Brand Guide', 'Visual Identity', 'Assets'] },
    { label: 'What\'s your business name, and what does it mean?', name: 'business_name_meaning', type: 'textarea', required: true },
    { label: 'What products or services do you offer?', name: 'products_services', type: 'textarea', required: true },
    { label: 'Who is your ideal customer or audience?', name: 'ideal_customer', type: 'textarea', required: true },
    { label: 'How would you describe your brand in 3–5 words?', name: 'brand_description', type: 'text', required: true },
    { label: 'What brands do you admire and why?', name: 'admired_brands', type: 'textarea', required: false },
    { label: 'Do you have a color palette in mind or want suggestions?', name: 'color_palette', type: 'textarea', required: false },
    { label: 'Are there any colors, fonts, symbols, or imagery you want to avoid?', name: 'avoid_elements', type: 'textarea', required: false },
    { label: 'Do you have any existing branding (logo, colors, fonts) we should build on or discard?', name: 'existing_branding', type: 'textarea', required: false },
    { label: 'Do you prefer a modern, vintage, minimalist, bold, or elegant look?', name: 'brand_style', type: 'select', required: true, options: ['Modern', 'Vintage', 'Minimalist', 'Bold', 'Elegant', 'Other'] },
  ],
  'illustration': [
    { label: 'What type of illustration do you need?', name: 'type', type: 'select', required: true, options: ['Custom Illustration', 'Icon', 'Asset'] },
    { label: 'What are the illustrations/icons for? (App, website, presentation, print, etc.)', name: 'purpose', type: 'textarea', required: true },
    { label: 'How many illustrations or icons do you need?', name: 'quantity', type: 'number', required: true },
    { label: 'Where will they appear (e.g., dark background, light background, both)?', name: 'background_context', type: 'text', required: true },
    { label: 'Do you have a preferred style? (Flat, outline, filled, hand-drawn, isometric, 3D, etc.)', name: 'style_preference', type: 'select', required: true, options: ['Flat', 'Outline', 'Filled', 'Hand-drawn', 'Isometric', '3D', 'Other'] },
    { label: 'Any reference examples or design inspiration you like?', name: 'references', type: 'textarea', required: false },
    { label: 'Should they match an existing brand or design system?', name: 'brand_match', type: 'text', required: false },
    { label: 'Do you want them colorful, monochrome?', name: 'color_scheme', type: 'select', required: true, options: ['Colorful', 'Monochrome', 'Both'] },
    { label: 'What dimensions or resolution do you need? (e.g., 24x24px, 512x512px)', name: 'dimensions', type: 'text', required: true },
    { label: 'What file formats do you require? (SVG, PNG, AI, EPS, etc.)', name: 'file_formats', type: 'textarea', required: true },
  ],
  'presentation-design': [
    { label: 'What type of presentation do you need?', name: 'type', type: 'select', required: true, options: ['Pitch Deck', 'Slide', 'Keynote', 'PowerPoint'] },
    { label: 'What is the topic of your presentation?', name: 'topic', required: true },
    { label: 'How many slides?', name: 'slides', type: 'number' },
  ],
  'web-development': [
    { label: 'What type of web development do you need?', name: 'type', type: 'select', required: true, options: ['Front-end', 'Landing Page', 'Integration'] },
    // 🧠 1. Vision & Purpose
    { label: 'What problem does the app solve?', name: 'problem', type: 'textarea', required: true },
    { label: 'Who is the target user?', name: 'target_user', type: 'textarea', required: true },
    { label: 'What is the main goal of the app (MVP)?', name: 'main_goal', type: 'textarea', required: true },
    { label: 'Are there similar apps you like/dislike? Why?', name: 'similar_apps', type: 'textarea' },
    // 📱 2. Features & Functionality
    { label: 'What are the core features you want in version 1?', name: 'core_features', type: 'textarea', required: true },
    { label: 'Any specific user flows or use cases you\'ve mapped out?', name: 'user_flows', type: 'textarea' },
    { label: 'Do you need user authentication? (Email, social logins, etc.)', name: 'user_auth', type: 'text' },
    { label: 'Will there be any in-app payments or subscriptions?', name: 'payments', type: 'text' },
    { label: 'Are there any integrations (APIs, third-party services)?', name: 'integrations', type: 'textarea' },
    // 🎨 3. Design & User Experience
    { label: 'Do you already have branding or UI/UX designs?', name: 'branding_uiux', type: 'text' },
    { label: 'Are you open to a custom UI or want to follow platform guidelines (Material, iOS HIG)?', name: 'ui_guidelines', type: 'text' },
    { label: 'What are your design inspirations (apps, websites)?', name: 'design_inspirations', type: 'textarea' },
  ],
  'email-development': [
    { label: 'What type of email development do you need?', name: 'type', type: 'select', required: true, options: ['Custom Template', 'Responsive Template'] },
    { label: 'What is the primary goal of this email? (e.g. promotion, newsletter, onboarding, announcement)', name: 'email_goal', type: 'select', required: true, options: ['Promotion', 'Newsletter', 'Onboarding', 'Announcement', 'Other'] },
    { label: 'Who is the target audience?', name: 'target_audience', type: 'textarea', required: true },
    { label: 'Do you have brand guidelines or existing email templates?', name: 'brand_guidelines', type: 'text', required: false },
    { label: 'Are there preferred colors, fonts, or visual styles?', name: 'visual_preferences', type: 'textarea', required: false },
    { label: 'Any reference emails or competitors you like the look of?', name: 'references', type: 'textarea', required: false },
    { label: 'What sections should be included? (e.g., hero image, headline, body, CTA, footer)', name: 'email_sections', type: 'textarea', required: true },
    { label: 'Do you have the copy and images ready?', name: 'content_ready', type: 'select', required: true, options: ['Yes, all content is ready', 'Partially ready', 'Need placeholder content'] },
    { label: 'Do you need placeholder content for now?', name: 'placeholder_content', type: 'checkbox', required: false },
    { label: 'Will there be personalized fields (e.g., "Hi [First Name]")?', name: 'personalization', type: 'textarea', required: false },
  ],
};

const typeTitles: Record<string, string> = {
  'graphic-design': 'Graphic Design',
  'web-design': 'Web Design',
  'ui-ux-design': 'UI/UX Design',
  'branding': 'Branding',
  'illustration': 'Illustration',
  'presentation-design': 'Presentation Design',
  'web-development': 'Web Development',
  'email-development': 'Email Development',
};

const RequestForm: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const { user } = useAuth();
  const questions = requestTypeQuestions[type || ''] || [];
  const [form, setForm] = useState<Record<string, any>>({});
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [brands, setBrands] = useState<any[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Helper function to check if a question should be shown
  const shouldShowQuestion = (question: any) => {
    if (!question.showIf) return true;
    
    const { field, value } = question.showIf;
    const fieldValue = form[field];
    
    if (Array.isArray(value)) {
      return value.includes(fieldValue);
    }
    
    return fieldValue === value;
  };

  // Filter questions based on conditions
  const visibleQuestions = questions.filter(shouldShowQuestion);

  useEffect(() => {
    if (!user) return;
    const fetchBrands = async () => {
      setLoadingBrands(true);
      const { data, error } = await supabase
        .from('brands')
        .select('id, name')
        .eq('user_id', user.id);
      if (!error) setBrands(data || []);
      setLoadingBrands(false);
    };
    fetchBrands();
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type: inputType, checked, files } = e.target as any;
    setForm((prev) => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : inputType === 'file' ? files[0] : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSubmitting(true);
    try {
      // Prepare answers, excluding brand_id
      const { brand_id, ...rawAnswers } = form;
      
      // Create structured questions and answers
      const questionsAndAnswers = visibleQuestions.map(question => ({
        question: question.label,
        answer: rawAnswers[question.name] || '',
        fieldName: question.name,
        fieldType: question.type || 'text',
        required: question.required || false
      }));

      const { error: insertError } = await supabase.from('service_requests').insert([
        {
          user_id: user?.id,
          brand_id: brand_id,
          type,
          answers: rawAnswers, // Keep original format for backward compatibility
          questions_and_answers: questionsAndAnswers, // New structured format
        },
      ]);
      if (insertError) {
        setError('Failed to submit request: ' + insertError.message);
      } else {
        setSuccess(true);
        setForm({});
      }
    } catch (err: any) {
      setError('Failed to submit request: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!type || !visibleQuestions.length) {
    return <div className="text-gray-500">Invalid request type.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-xl shadow border p-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{typeTitles[type]}</h1>
      {success ? (
        <div className="mb-4 p-4 text-green-700 bg-green-100 rounded-lg">Request submitted!</div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Brand<span className="text-red-500 ml-1">*</span></label>
            <select
              name="brand_id"
              value={form.brand_id || ''}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              disabled={loadingBrands}
            >
              <option value="" disabled>{loadingBrands ? 'Loading...' : 'Select a brand'}</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>{brand.name}</option>
              ))}
            </select>
          </div>
          {error && (
            <div className="mb-4 p-4 text-red-700 bg-red-100 rounded-lg">{error}</div>
          )}
          {visibleQuestions.map((q) => (
            <div key={q.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {q.label}
                {q.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {q.type === 'textarea' ? (
                <textarea
                  name={q.name}
                  required={q.required}
                  value={form[q.name] || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              ) : q.type === 'select' ? (
                <select
                  name={q.name}
                  required={q.required}
                  value={form[q.name] || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                >
                  <option value="" disabled>Select an option</option>
                  {q.options?.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : q.type === 'checkbox' ? (
                <input
                  type="checkbox"
                  name={q.name}
                  checked={!!form[q.name]}
                  onChange={handleChange}
                  className="mr-2"
                />
              ) : q.type === 'file' ? (
                <input
                  type="file"
                  name={q.name}
                  onChange={handleChange}
                  className="block w-full text-sm text-gray-500"
                />
              ) : (
                <input
                  type={q.type || 'text'}
                  name={q.name}
                  required={q.required}
                  value={form[q.name] || ''}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              )}
            </div>
          ))}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-white px-6 py-3 rounded-md font-semibold hover:bg-primary/90 transition duration-300"
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      )}
    </div>
  );
};

export default RequestForm; 